/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, Dialog, DialogContent, Checkbox } from "@mui/material";
import PropTypes from "prop-types";
import { DataGrid } from "@mui/x-data-grid";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  postPagerDutyIntegration,
  getSitePagerDutyServicesQuery,
  putSitePagerDutyServices,
} from "../../lib/Queries";
import { useSnackbar } from "notistack";
import PagerDutySetupDialog from "./PagerDutySetupDialog";

const ManagePagerDutyIntegrationModal = ({
  open,
  onClose,
  currentSite,
  pagerDutyServices = [],
  pagerDutyIntegrations = [],
}) => {
  const siteUuid = currentSite?.uuid;

  const [enabledIds, setEnabledIds] = useState(new Set());
  const [showPdSetup, setShowPdSetup] = useState(false);
  const [pdSetupServiceId, setPdSetupServiceId] = useState(null);
  const [pdSetupName, setPdSetupName] = useState("");
  const [pdSetupUrl, setPdSetupUrl] = useState("");
  const [pdSetupKey, setPdSetupKey] = useState("");

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data: sitePdServices } = useQuery({
    ...getSitePagerDutyServicesQuery(siteUuid),
    enabled: open && !!siteUuid,
  });

  useEffect(() => {
    if (sitePdServices?.pagerduty_service_ids) {
      setEnabledIds(new Set(sitePdServices.pagerduty_service_ids));
    }
  }, [sitePdServices]);

  const pdIntegrationMutation = useMutation({
    mutationFn: ({ id, name, integration_key }) =>
      postPagerDutyIntegration(id, { name, integration_key }),
    onSuccess: () => {
      enqueueSnackbar("PagerDuty integration saved.", {
        autoHideDuration: 3000,
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["pd-configured"] });
      setShowPdSetup(false);
    },
    onError: () => {
      enqueueSnackbar("Failed to save PagerDuty integration.", {
        autoHideDuration: 4000,
        variant: "error",
      });
    },
  });

  const saveSiteServicesMutation = useMutation({
    mutationFn: (serviceIds) => putSitePagerDutyServices(siteUuid, serviceIds),
    onSuccess: () => {
      enqueueSnackbar("Site PagerDuty services updated.", {
        autoHideDuration: 3000,
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["site-pd-services", siteUuid] });
      onClose();
    },
    onError: () => {
      enqueueSnackbar("Failed to update site PagerDuty services.", {
        autoHideDuration: 4000,
        variant: "error",
      });
    },
  });

  const handleToggleEnabled = useCallback((id) => {
    setEnabledIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const rows = useMemo(
    () =>
      (pagerDutyServices || []).map((s) => {
        const cfg = (pagerDutyIntegrations || []).find((c) => c.id === s.id);
        const isSetUp = Boolean(cfg && cfg.name && cfg.integration_key);
        return {
          id: s.id,
          service: s.name,
          isSetUp,
          enabled: isSetUp && enabledIds.has(s.id),
          url: s.url || "",
          integration_key: cfg?.integration_key || "",
        };
      }),
    [pagerDutyServices, pagerDutyIntegrations, enabledIds],
  );

  const enabledServices = useMemo(
    () => rows.filter((r) => r.enabled).map((r) => r.service),
    [rows],
  );

  const columns = useMemo(
    () => [
      { field: "service", headerName: "Service", flex: 3, sortable: false },
      {
        field: "isSetUp",
        headerName: "Manage",
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const { id, service, isSetUp, url, integration_key } = params.row;
          const handleClick = () => {
            setPdSetupServiceId(id);
            setPdSetupName(service);
            setPdSetupUrl(url);
            setPdSetupKey(isSetUp ? integration_key : "");
            setShowPdSetup(true);
          };
          return isSetUp ? (
            <Button
              variant="outlined"
              color="inherit"
              size="small"
              onClick={handleClick}
              sx={{
                textTransform: "none",
                borderColor: "grey.300",
                color: "text.primary",
              }}
            >
              Edit
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={handleClick}
              sx={{
                textTransform: "none",
                boxShadow: "none",
                bgcolor: "grey.900",
                color: "common.white",
                "&:hover": { bgcolor: "grey.800", boxShadow: "none" },
              }}
            >
              Set Up
            </Button>
          );
        },
      },
      {
        field: "enabled",
        headerName: "This Site",
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <Checkbox
            checked={Boolean(params.row.enabled)}
            disabled={!params.row.isSetUp}
            onChange={() => handleToggleEnabled(params.row.id)}
            color="primary"
            inputProps={{ "aria-label": `Enable ${params.row.service} for this site` }}
            sx={{
              "& .MuiSvgIcon-root": { width: 20, height: 20 },
            }}
          />
        ),
      },
    ],
    [handleToggleEnabled],
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ padding: "2rem" }}>
          <Box sx={{ mb: "2rem" }}>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Manage Integration
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              PagerDuty Services
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: "text.secondary", mb: "2rem" }}>
            Enabling a service for {currentSite?.name ? `“${currentSite.name}”` : "this site"} makes
            it available for use in Alarm Action automations at this site.
          </Typography>
          <DataGrid
            rows={rows}
            columns={columns}
            hideFooter
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnResize
            disableColumnSelector
            sx={{
              "& .MuiDataGrid-columnSeparator": { display: "none" },
              "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" },
              '& .MuiDataGrid-columnHeader[data-field="enabled"] .MuiDataGrid-columnHeaderTitleContainer':
                {
                  justifyContent: "end",
                  width: "100%",
                },
              '& .MuiDataGrid-cell[data-field="enabled"]': {
                justifyContent: "flex-end",
              },
              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
              maxHeight: "250px",
              overflow: "auto",
              mb: "2rem",
              border: "none",
              borderRadius: "0",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          />

          <Box
            sx={{
              mb: "2rem",
              backgroundColor: "#F5F5F5",
              borderRadius: "4px",
              p: "1.5rem",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              minHeight: "5.5rem",
            }}
          >
            {enabledServices.length === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Select services to apply to this site.
              </Typography>
            ) : (
              <>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Services Applied to This Site
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {enabledServices.join(", ")}
                </Typography>
              </>
            )}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button variant="outlined" color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={saveSiteServicesMutation.isLoading}
              onClick={() => saveSiteServicesMutation.mutate([...enabledIds])}
            >
              Save Changes
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <PagerDutySetupDialog
        open={showPdSetup}
        onClose={() => {
          setShowPdSetup(false);
          setPdSetupServiceId(null);
        }}
        serviceName={pdSetupName}
        serviceUrl={pdSetupUrl}
        initialIntegrationKey={pdSetupKey}
        onSave={(integrationKey) => {
          if (!pdSetupServiceId) {
            enqueueSnackbar("No service selected.", {
              autoHideDuration: 3000,
              variant: "warning",
            });
            return;
          }
          pdIntegrationMutation.mutate({
            id: pdSetupServiceId,
            name: pdSetupName,
            integration_key: integrationKey,
          });
        }}
        isSaving={pdIntegrationMutation.isLoading}
      />
    </>
  );
};

ManagePagerDutyIntegrationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  currentSite: PropTypes.object,
  pagerDutyServices: PropTypes.array,
  pagerDutyIntegrations: PropTypes.array,
};

export default ManagePagerDutyIntegrationModal;
